import React from 'react';
import { X, User, MapPin, FileText, Star } from 'lucide-react';
import type { Driver, Vehicle } from '../lib/types';

export type DriverEditForm = {
  full_name: string;
  email: string;
  phone: string;
  driver_code: string;
  license_no: string;
  vehicle_id: string;
};

type DriverProfileModalProps = {
  driver: Driver;
  open: boolean;
  onClose: () => void;
  activeTab: string;
  onTabChange: (tab: string) => void;
  editMode: boolean;
  onEditModeChange: (edit: boolean) => void;
  editForm: DriverEditForm;
  setEditForm: React.Dispatch<React.SetStateAction<DriverEditForm>>;
  editAvailableVehicles: Vehicle[];
  onSave: () => void;
  onResetPassword: (driver: Driver, sendEmail: boolean) => void;
};

const profileTabs = [
  { id: 'profile', label: 'Profile', icon: <User className="w-4 h-4" /> },
  { id: 'trips', label: 'Trip History', icon: <MapPin className="w-4 h-4" /> },
  { id: 'documents', label: 'Documents', icon: <FileText className="w-4 h-4" /> },
];

const DriverProfileModal: React.FC<DriverProfileModalProps> = ({
  driver,
  open,
  onClose,
  activeTab,
  onTabChange,
  editMode,
  onEditModeChange,
  editForm,
  setEditForm,
  editAvailableVehicles,
  onSave,
  onResetPassword,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-modal w-full max-w-4xl max-h-[90vh] flex flex-col animate-slide-up overflow-hidden">
        <div
          className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}
        >
          <h2 className="font-bold text-white text-lg" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
            {driver.full_name.toUpperCase()}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="border-b border-slate-100 flex overflow-x-auto shrink-0">
          {profileTabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors relative ${
                activeTab === tab.id ? 'text-electric-600' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
              }`}
            >
              {tab.icon}
              {tab.label}
              {activeTab === tab.id && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-electric-500 rounded-t" />
              )}
            </button>
          ))}
        </div>
        <div className="p-6 overflow-y-auto">
          {activeTab === 'profile' && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center gap-3">
                <div className="w-36 h-36 rounded-2xl bg-gradient-to-br from-electric-100 to-electric-50 flex items-center justify-center ring-4 ring-slate-100">
                  <span className="text-5xl font-bold text-electric-500" style={{ fontFamily: 'Space Grotesk' }}>
                    {driver.full_name.charAt(0).toUpperCase()}
                  </span>
                </div>
                {!editMode && (
                  <button type="button" onClick={() => onEditModeChange(true)} className="btn-primary text-sm px-4 py-2">
                    Edit Profile
                  </button>
                )}
              </div>
              <div className="md:col-span-2">
                {editMode ? (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800">Edit Profile</h3>
                    {[
                      { label: 'Full Name', key: 'full_name' as const, type: 'text' },
                      { label: 'Förarkod (taxilegitimation)', key: 'driver_code' as const, type: 'text' },
                      { label: 'Email', key: 'email' as const, type: 'email' },
                      { label: 'Phone', key: 'phone' as const, type: 'text' },
                      { label: 'Körkort', key: 'license_no' as const, type: 'text' },
                    ].map((f) => (
                      <div key={f.key}>
                        <label className="block text-sm font-medium text-slate-600 mb-1.5">{f.label}</label>
                        <input
                          type={f.type}
                          value={editForm[f.key]}
                          onChange={(e) => setEditForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                          className="input-premium"
                        />
                      </div>
                    ))}
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">Vehicle</label>
                      <select
                        value={editForm.vehicle_id}
                        onChange={(e) => setEditForm((prev) => ({ ...prev, vehicle_id: e.target.value }))}
                        className="input-premium"
                      >
                        <option value="">No vehicle</option>
                        {editAvailableVehicles.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.plate} — {v.model}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button type="button" onClick={onSave} className="btn-primary text-sm px-5 py-2">
                        Save Changes
                      </button>
                      <button type="button" onClick={() => onEditModeChange(false)} className="btn-ghost text-sm">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { label: 'Förarkod (taxilegitimation)', value: driver.driver_code ?? '—' },
                      { label: 'Full Name', value: driver.full_name },
                      { label: 'Email', value: driver.email ?? '—' },
                      { label: 'Phone', value: driver.phone ?? '—' },
                      { label: 'Körkort', value: driver.license_no ?? '—' },
                      {
                        label: 'Vehicle',
                        value: driver.vehicle ? `${driver.vehicle.plate} (${driver.vehicle.model})` : '—',
                      },
                      { label: 'Joined', value: new Date(driver.created_at).toLocaleDateString() },
                      { label: 'Status', value: driver.status, capitalize: true },
                      { label: 'Total Trips', value: String(driver.total_trips) },
                    ].map((f, i) => (
                      <div key={i} className="bg-slate-50 rounded-xl p-3.5">
                        <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">{f.label}</div>
                        <div className={`text-sm font-semibold text-slate-800 ${f.capitalize ? 'capitalize' : ''}`}>
                          {f.value}
                        </div>
                      </div>
                    ))}
                    <div className="bg-slate-50 rounded-xl p-3.5">
                      <div className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-1">Performance Score</div>
                      <div className="flex items-center gap-1.5">
                        <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                        <span className="font-semibold">{driver.performance_score}%</span>
                      </div>
                    </div>
                    {driver.user_id && (
                      <div className="md:col-span-2 flex flex-wrap gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => onResetPassword(driver, true)}
                          className="btn-primary text-xs px-4 py-2"
                        >
                          Skicka inloggningsmail
                        </button>
                        <button
                          type="button"
                          onClick={() => onResetPassword(driver, false)}
                          className="btn-ghost text-xs px-4 py-2 border border-slate-200"
                        >
                          Återställ lösenord
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'trips' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-slate-800">Trip History</h3>
                <p className="text-xs text-slate-400">View in Tachograph for full detail</p>
              </div>
              <div className="text-sm text-slate-500 py-8 text-center bg-slate-50 rounded-xl">
                Navigate to <strong>Tachograph → {driver.full_name}</strong> for full trip history with map.
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-slate-800">Driver Documents</h3>
                <button type="button" className="btn-primary text-xs px-3 py-1.5">
                  Upload New Document
                </button>
              </div>
              <div className="rounded-xl border border-slate-100 overflow-hidden">
                <table className="min-w-full table-premium">
                  <thead>
                    <tr>
                      <th>Document</th>
                      <th>Status</th>
                      <th>Expiry Date</th>
                      <th>Upload Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      {
                        name: 'Driver License',
                        status: 'Verified',
                        expiry: '—',
                        upload: new Date(driver.created_at).toLocaleDateString(),
                      },
                      { name: 'Vehicle Registration', status: 'Pending', expiry: '—', upload: '—' },
                      { name: 'Insurance Document', status: 'Pending', expiry: '—', upload: '—' },
                    ].map((doc, i) => (
                      <tr key={i}>
                        <td className="font-medium text-slate-800">{doc.name}</td>
                        <td>
                          <span className={doc.status === 'Verified' ? 'badge-verified' : 'badge-pending'}>
                            {doc.status}
                          </span>
                        </td>
                        <td>{doc.expiry}</td>
                        <td>{doc.upload}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DriverProfileModal;
